/**
 * Refresh exactly the queries that draw these addresses — the one helper a
 * finished piece of address work calls (research finishing for a subject,
 * pushed or polled), rather than each surface naming the keys it happens to
 * know.
 *
 * What it reaches, per address:
 *
 * - every query in the address group (`queryResourcePolicies`, `group:
 *   "address"`) whose KEY MENTIONS the address — the per-ref overlay entry
 *   (`address-overlay-ref`), the REST overlay read, the cross-chain reads, the
 *   assignments and entity mapping, on every chain. Every chain on purpose:
 *   research fans out to the chains an address turns out to be live on, and
 *   its findings land there too. Matched on the key's text, not on a per-family
 *   key shape, so a batch key that carries the address inside a joined ref
 *   string is reached as well, and a family added to the group later is covered
 *   without touching this file;
 * - the ApiClient's 30 s GET cache for any URL naming the address — otherwise
 *   the refetch above is answered with the pre-research body;
 * - the address's kept overlay answer on disk (`addressOverlayDiskCache`), which
 *   a remount would otherwise paint as trusted for ten minutes;
 * - `catalog-names` and the review queue (`seed-reviews`), which are not keyed
 *   by address: a finding can name a new entity or label, or wait in Review.
 *
 * Only ACTIVE queries refetch now; an inactive one is marked stale and re-reads
 * when a surface mounts it. That is what keeps one finished subject from being
 * a refetch storm: the cost is the surfaces on screen that draw that address.
 */
import * as React from "react";
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { getAddress } from "viem";
import { useApiClient } from "@authe/ui-core/api";
import { normalizeAddress } from "@authe/fe-commons/chains";
import { overlayDiskCache } from "./addressOverlayDiskCache";
import { queryResourcePolicies } from "@/api/queryResourcePolicies";

export interface AddressQuerySubject {
  /** The chain it was researched on; `null` when unknown. Matching ignores it — see above. */
  chainId: string | null;
  address: string;
}

/** Families an address's outcome changes that are not keyed by address. */
export const ADDRESS_OUTCOME_SHARED_FAMILIES: readonly string[] = ["catalog-names", "seed-reviews"];

const EVM_HEX = /^0x[0-9a-fA-F]{40}$/;

/** How a subject is looked for in a key's text: folded per its family's case rule. */
interface Needle {
  text: string;
  /** Compare against the lower-cased key text (EVM hex folds; base58 does not). */
  folded: boolean;
}

function needleOf(subject: AddressQuerySubject): Needle | null {
  const address = subject.address.trim();
  if (!address) return null;
  const normal = normalizeAddress(address, subject.chainId);
  const folded = normal === normal.toLowerCase() && /^0x/i.test(address);
  return { text: folded ? normal.toLowerCase() : normal, folded };
}

function addressGroupFamilies(): ReadonlySet<string> {
  return new Set(
    Object.entries(queryResourcePolicies)
      .filter(([, entry]) => entry.group === "address")
      .map(([family]) => family),
  );
}

/**
 * The predicate: does this query draw one of the subjects? Pure, so the
 * matching rule is testable without a client.
 */
export function addressQueryMatcher(subjects: readonly AddressQuerySubject[]): (key: QueryKey) => boolean {
  const needles = subjects.map(needleOf).filter((n): n is Needle => n !== null);
  const families = addressGroupFamilies();
  return (key) => {
    const family = String(key[0]);
    if (ADDRESS_OUTCOME_SHARED_FAMILIES.includes(family)) return needles.length > 0;
    if (!families.has(family)) return false;
    let text: string;
    try {
      text = JSON.stringify(key.slice(1)) ?? "";
    } catch {
      return false;
    }
    const lower = text.toLowerCase();
    return needles.some((needle) => (needle.folded ? lower : text).includes(needle.text));
  };
}

/** Every spelling a cached URL may carry the address in — the GET cache matches case-sensitively. */
export function addressUrlSpellings(address: string): string[] {
  const out = new Set([address.trim()]);
  if (EVM_HEX.test(address.trim())) {
    out.add(address.trim().toLowerCase());
    try {
      out.add(getAddress(address.trim().toLowerCase()));
    } catch {
      /* Not checksummable: the two spellings above are all there is. */
    }
  }
  return [...out].filter(Boolean);
}

export interface AddressQueryInvalidationDeps {
  queryClient: QueryClient;
  api?: { invalidateMatching: (fragment: string) => void } | null;
}

/** Refresh what draws these addresses. A no-op for an empty list. */
export function invalidateAddressQueries(
  deps: AddressQueryInvalidationDeps,
  subjects: readonly AddressQuerySubject[],
): void {
  if (subjects.length === 0) return;
  const unique = new Map<string, AddressQuerySubject>();
  for (const subject of subjects) {
    const needle = needleOf(subject);
    if (needle) unique.set(needle.text, subject);
  }
  if (unique.size === 0) return;
  const list = [...unique.values()];
  for (const subject of list) {
    for (const spelling of addressUrlSpellings(subject.address)) deps.api?.invalidateMatching(spelling);
  }
  deps.api?.invalidateMatching("/seed-reviews");
  void forgetKeptOverlays(list);
  const matches = addressQueryMatcher(list);
  void deps.queryClient.invalidateQueries({
    predicate: (query) => matches(query.queryKey),
    refetchType: "active",
  });
}

async function forgetKeptOverlays(subjects: readonly AddressQuerySubject[]): Promise<void> {
  const disk = overlayDiskCache();
  if (!disk) return;
  for (const subject of subjects) await disk.forgetAddress(subject.chainId, subject.address);
}

/** {@link invalidateAddressQueries} bound to this tree's clients — stable across renders. */
export function useInvalidateAddressQueries(): (subjects: readonly AddressQuerySubject[]) => void {
  const queryClient = useQueryClient();
  const api = useApiClient();
  return React.useCallback(
    (subjects: readonly AddressQuerySubject[]) => invalidateAddressQueries({ queryClient, api }, subjects),
    [queryClient, api],
  );
}
